import React, { useState, useEffect, useRef } from 'react';

// Declare Leaflet types for TypeScript
declare global {
  interface Window {
    L: any;
  }
}

// OpenStreetMap place interface (free, no billing required)
interface Place {
  place_id: number;
  name: string;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  class: string;
  icon?: string;
  address?: {
    road?: string;
    neighbourhood?: string;
    suburb?: string;
    city?: string;
    state?: string;
    postcode?: string;
  };
  extratags?: {
    phone?: string;
    website?: string;
    opening_hours?: string;
  };
}

// World Clock Component
const WorldClock: React.FC<{ timezone: string; city: string; isAnalog?: boolean }> = ({ timezone, city, isAnalog = false }) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const getTimeInTimezone = () => {
    return time.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const getDateInTimezone = () => {
    return time.toLocaleDateString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAnalogTime = () => {
    const dateInTimezone = new Date(time.toLocaleString('en-US', { timeZone: timezone }));
    const hours = dateInTimezone.getHours() % 12;
    const minutes = dateInTimezone.getMinutes();
    const seconds = dateInTimezone.getSeconds();

    return {
      hourRotation: (hours * 30) + (minutes * 0.5),
      minuteRotation: minutes * 6,
      secondRotation: seconds * 6
    };
  };

  if (isAnalog) {
    const { hourRotation, minuteRotation, secondRotation } = getAnalogTime();
    return (
      <div className="flex flex-col items-center gap-2 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
        <div className="relative w-32 h-32">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            {/* Clock face */}
            <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-300 dark:text-gray-600" />
            {/* Hour markers */}
            {[...Array(12)].map((_, i) => {
              const angle = (i * 30 - 90) * (Math.PI / 180);
              const x1 = 50 + 40 * Math.cos(angle);
              const y1 = 50 + 40 * Math.sin(angle);
              const x2 = 50 + 45 * Math.cos(angle);
              const y2 = 50 + 45 * Math.sin(angle);
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="2" className="text-gray-400 dark:text-gray-500" />;
            })}
            {/* Hour hand */}
            <line
              x1="50" y1="50"
              x2={50 + 25 * Math.sin(hourRotation * Math.PI / 180)}
              y2={50 - 25 * Math.cos(hourRotation * Math.PI / 180)}
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              className="text-gray-700 dark:text-gray-300"
            />
            {/* Minute hand */}
            <line
              x1="50" y1="50"
              x2={50 + 35 * Math.sin(minuteRotation * Math.PI / 180)}
              y2={50 - 35 * Math.cos(minuteRotation * Math.PI / 180)}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="text-blue-600 dark:text-blue-400"
            />
            {/* Second hand */}
            <line
              x1="50" y1="50"
              x2={50 + 38 * Math.sin(secondRotation * Math.PI / 180)}
              y2={50 - 38 * Math.cos(secondRotation * Math.PI / 180)}
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              className="text-red-500"
            />
            {/* Center dot */}
            <circle cx="50" cy="50" r="3" fill="currentColor" className="text-gray-700 dark:text-gray-300" />
          </svg>
        </div>
        <div className="text-center">
          <div className="text-xs font-semibold text-gray-900 dark:text-white">{city}</div>
          <div className="text-xs text-gray-600 dark:text-gray-400">{getDateInTimezone()}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1 p-3 bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
      <div className="text-sm font-semibold text-gray-900 dark:text-white">{city}</div>
      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 font-mono">{getTimeInTimezone()}</div>
      <div className="text-xs text-gray-600 dark:text-gray-400">{getDateInTimezone()}</div>
    </div>
  );
};

interface LocationInfo {
  ip: string;
  city: string;
  region: string;
  country: string;
  isp: string;
  lat: number;
  lng: number;
}

export const PlaceFinder: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('coffee');
  const [places, setPlaces] = useState<Place[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationInfo, setLocationInfo] = useState<LocationInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showClocks, setShowClocks] = useState(true);
  const [clockType, setClockType] = useState<'analog' | 'digital'>('digital');
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);

  // Initialize Leaflet map
  useEffect(() => {
    // Load Leaflet CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Load Leaflet JS
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = initializeMap;
      document.body.appendChild(script);
    } else {
      initializeMap();
    }
  }, []);

  // Fetch user's IP and location info
  const fetchLocationInfo = async (location: { lat: number; lng: number }) => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      if (response.ok) {
        const data = await response.json();
        setLocationInfo({
          ip: data.ip || 'Unknown',
          city: data.city || 'Unknown',
          region: data.region || 'Unknown',
          country: data.country_name || 'Unknown',
          isp: data.org || 'Unknown',
          lat: location.lat,
          lng: location.lng
        });
      }
    } catch (error) {
      console.error('Error fetching location info:', error);
    }
  };

  const initializeMap = () => {
    if (mapContainerRef.current && !mapInstanceRef.current && window.L) {
      // Get user location first, then initialize map
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setUserLocation(location);
            fetchLocationInfo(location);
            
            // Start with world view (zoomed out)
            const map = window.L.map(mapContainerRef.current).setView([20, 0], 2);
            
            // Add OpenStreetMap tiles
            window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
              attribution: '© OpenStreetMap contributors',
              maxZoom: 19,
            }).addTo(map);

            // Add blue pulsing marker for user's current location
            window.L.marker([location.lat, location.lng], {
              icon: window.L.divIcon({
                className: 'user-location-marker',
                html: '<div style="background: #3B82F6; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 10px rgba(59, 130, 246, 0.5); animation: pulse 2s infinite;"></div><style>@keyframes pulse { 0%, 100% { box-shadow: 0 0 10px rgba(59, 130, 246, 0.5); } 50% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.8); transform: scale(1.1); } }</style>',
                iconSize: [20, 20],
                iconAnchor: [10, 10]
              })
            })
            .addTo(map)
            .bindPopup('<b>📍 Your Current Location</b>');

            mapInstanceRef.current = map;
            
            // Smooth zoom animation from world to user location (2 seconds)
            setTimeout(() => {
              map.flyTo([location.lat, location.lng], 13, {
                duration: 2, // 2 seconds animation
                easeLinearity: 0.25
              });
              
              // After zoom animation, search nearby places
              setTimeout(() => {
                searchNearbyPlaces('coffee', location);
              }, 2200);
            }, 500); // Small delay before starting zoom
          },
          (error) => {
            console.error('Error getting location:', error);
            // Fallback: Create map with default location if geolocation fails
            const defaultLocation = { lat: 20.5937, lng: 78.9629 }; // Center of India
            setUserLocation(defaultLocation);
            setError('📍 Please enable location access for personalized results.');
            
            const map = window.L.map(mapContainerRef.current).setView([defaultLocation.lat, defaultLocation.lng], 5);
            
            window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
              attribution: '© OpenStreetMap contributors',
              maxZoom: 19,
            }).addTo(map);

            mapInstanceRef.current = map;
            searchNearbyPlaces('coffee', defaultLocation);
          }
        );
      } else {
        // Browser doesn't support geolocation
        const defaultLocation = { lat: 20.5937, lng: 78.9629 };
        setUserLocation(defaultLocation);
        setError('📍 Geolocation is not supported by your browser.');
        
        const map = window.L.map(mapContainerRef.current).setView([defaultLocation.lat, defaultLocation.lng], 5);
        
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19,
        }).addTo(map);

        mapInstanceRef.current = map;
        searchNearbyPlaces('coffee', defaultLocation);
      }
    }
  };

  const searchNearbyPlaces = async (query: string, location: { lat: number; lng: number }) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Use Nominatim API (FREE OpenStreetMap geocoding service)
      // No API key or billing required!
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&` +
        `lat=${location.lat}&lon=${location.lng}&` +
        `format=json&addressdetails=1&extratags=1&limit=20`,
        {
          headers: {
            'User-Agent': 'AJ-Studioz-Creative-Suite/1.0'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to search places');
      }

      const data = await response.json();
      setPlaces(data);
      
      // Update map with markers
      if (mapInstanceRef.current && data.length > 0) {
        // Clear existing markers
        mapInstanceRef.current.eachLayer((layer: any) => {
          if (layer instanceof window.L.Marker) {
            mapInstanceRef.current.removeLayer(layer);
          }
        });

        // Add markers for each place
        data.forEach((place: Place) => {
          const marker = window.L.marker([parseFloat(place.lat), parseFloat(place.lon)])
            .addTo(mapInstanceRef.current)
            .bindPopup(`<b>${place.name || place.display_name.split(',')[0]}</b><br>${place.display_name}`);
          
          marker.on('click', () => setSelectedPlace(place));
        });

        // Fit map to show all markers
        const bounds = data.map((p: Place) => [parseFloat(p.lat), parseFloat(p.lon)]);
        mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
      }
      
    } catch (err) {
      console.error('Search error:', err);
      setError(err instanceof Error ? err.message : 'Failed to search places');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    if (userLocation && searchQuery.trim()) {
      searchNearbyPlaces(searchQuery, userLocation);
    }
  };

  const getDirectionsUrl = (place: Place) => {
    return `https://www.openstreetmap.org/directions?from=${userLocation?.lat},${userLocation?.lng}&to=${place.lat},${place.lon}#map=15/${place.lat}/${place.lon}`;
  };

  const getPlaceIcon = (type: string) => {
    const iconMap: Record<string, string> = {
      cafe: '☕',
      restaurant: '🍽️',
      fast_food: '🍔',
      bar: '🍺',
      pub: '🍻',
      hotel: '🏨',
      hospital: '🏥',
      pharmacy: '💊',
      bank: '🏦',
      atm: '🏧',
      fuel: '⛽',
      parking: '🅿️',
      school: '🎓',
      university: '🎓',
      library: '📚',
      gym: '🏋️',
      cinema: '🎬',
      theatre: '🎭',
      museum: '🏛️',
      park: '🌳',
      shop: '🛍️',
      supermarket: '🛒',
    };
    return iconMap[type] || '📍';
  };

  const presetSearches = [
    { icon: '☕', label: 'Coffee Shops', query: 'coffee' },
    { icon: '🍕', label: 'Restaurants', query: 'restaurant' },
    { icon: '🏋️', label: 'Gyms', query: 'gym' },
    { icon: '🏥', label: 'Hospitals', query: 'hospital' },
    { icon: '🛒', label: 'Supermarkets', query: 'supermarket' },
    { icon: '⛽', label: 'Gas Stations', query: 'fuel' },
    { icon: '🏨', label: 'Hotels', query: 'hotel' },
    { icon: '🎬', label: 'Cinemas', query: 'cinema' },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-500 via-green-500 to-blue-600 bg-clip-text text-transparent mb-4">
          🗺️ Nearby Places Finder
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300">
          Discover coffee shops, restaurants, and more around you
        </p>
      </div>

      {/* Current Location Info Panel */}
      {locationInfo && (
        <div className="mb-6 bg-gradient-to-r from-blue-500 to-green-500 rounded-2xl shadow-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Your Current Location
            </h2>
            <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold">
              📡 LIVE
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide opacity-80 mb-1">IP Address</div>
              <div className="text-lg font-bold">{locationInfo.ip}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Location</div>
              <div className="text-lg font-bold">{locationInfo.city}, {locationInfo.region}</div>
              <div className="text-sm opacity-90">{locationInfo.country}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Coordinates</div>
              <div className="text-sm font-mono">
                <div>📍 Lat: {locationInfo.lat.toFixed(4)}°</div>
                <div>📍 Lng: {locationInfo.lng.toFixed(4)}°</div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide opacity-80 mb-1">ISP Provider</div>
              <div className="text-sm font-bold truncate">{locationInfo.isp}</div>
            </div>
          </div>
        </div>
      )}

      {/* World Clocks Section */}
      {showClocks && (
        <div className="mb-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              World Clock
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => setClockType(clockType === 'analog' ? 'digital' : 'analog')}
                className="px-4 py-2 text-sm bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-lg transition-colors font-medium"
              >
                {clockType === 'analog' ? '🔢 Digital' : '🕐 Analog'}
              </button>
              <button
                onClick={() => setShowClocks(false)}
                className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
              >
                Hide
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <WorldClock timezone="Asia/Kolkata" city="Your Location" isAnalog={clockType === 'analog'} />
            <WorldClock timezone="America/New_York" city="New York" isAnalog={clockType === 'analog'} />
            <WorldClock timezone="Europe/London" city="London" isAnalog={clockType === 'analog'} />
            <WorldClock timezone="Asia/Tokyo" city="Tokyo" isAnalog={clockType === 'analog'} />
            <WorldClock timezone="Australia/Sydney" city="Sydney" isAnalog={clockType === 'analog'} />
          </div>
        </div>
      )}

      {!showClocks && (
        <div className="mb-6 text-center">
          <button
            onClick={() => setShowClocks(true)}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-semibold rounded-xl transition-all shadow-lg"
          >
            🕐 Show World Clock
          </button>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mb-6 border border-gray-100 dark:border-gray-700">
        <div className="flex gap-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search for places... (e.g., coffee shop, restaurant, gym)"
            className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          />
          <button
            onClick={handleSearch}
            disabled={isLoading || !userLocation}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 disabled:from-gray-400 disabled:to-gray-400 text-white font-semibold rounded-xl transition-all shadow-lg disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Searching...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </>
            )}
          </button>
        </div>

        {/* Quick Search Presets */}
        <div className="mt-4 flex flex-wrap gap-2">
          {presetSearches.map((preset) => (
            <button
              key={preset.query}
              onClick={() => {
                setSearchQuery(preset.query);
                if (userLocation) searchNearbyPlaces(preset.query, userLocation);
              }}
              className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700/80 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-full transition-colors font-medium"
            >
              {preset.icon} {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Built with AJ STUDIOZ branding */}
      <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-3 border-2 border-gray-200 dark:border-gray-700 z-50">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Built with</span>
          <img 
            src="/aj-logo.jpg" 
            alt="AJ STUDIOZ" 
            className="w-6 h-6 rounded-lg object-cover shadow-md"
          />
          <span className="text-xs font-bold bg-gradient-to-r from-blue-500 to-green-500 bg-clip-text text-transparent">
            AJ STUDIOZ
          </span>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 mb-6">
          <p className="text-yellow-800 dark:text-yellow-200 text-sm">{error}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="bg-gradient-to-r from-blue-500 to-green-500 p-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Live Map View
                {locationInfo && (
                  <span className="ml-auto text-sm bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                    📍 {locationInfo.city}
                  </span>
                )}
              </h2>
            </div>
            <div 
              ref={mapContainerRef} 
              className="w-full h-[500px] bg-gray-100 dark:bg-gray-900"
              style={{ zIndex: 1 }}
            />
          </div>
        </div>

        {/* Places List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 max-h-[564px] overflow-y-auto border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <svg className="w-6 h-6 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Nearby Places ({places.length})
            </h2>
            
            {places.length === 0 && !isLoading && (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">No places found. Try a different search!</p>
              </div>
            )}

            <div className="space-y-3">
              {places.map((place) => (
                <div
                  key={place.place_id}
                  onClick={() => {
                    setSelectedPlace(place);
                    // Center map on selected place
                    if (mapInstanceRef.current) {
                      mapInstanceRef.current.setView([parseFloat(place.lat), parseFloat(place.lon)], 16);
                    }
                  }}
                  className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    selectedPlace?.place_id === place.place_id
                      ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 bg-white dark:bg-gray-800/50'
                  }`}
                >
                  {/* Place Icon & Type */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{getPlaceIcon(place.type)}</span>
                    <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700/80 text-gray-600 dark:text-gray-300 rounded-full font-medium">
                      {place.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  
                  <h3 className="font-bold text-gray-900 dark:text-white mb-1 text-base">
                    {place.name || place.display_name.split(',')[0]}
                  </h3>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
                    {place.display_name}
                  </p>

                  {/* Address Details */}
                  {place.address && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-3 space-y-1">
                      {place.address.road && (
                        <div>📍 {place.address.road}</div>
                      )}
                      {place.address.city && (
                        <div>🏙️ {place.address.city}{place.address.state ? `, ${place.address.state}` : ''}</div>
                      )}
                    </div>
                  )}

                  {/* Contact Info */}
                  {place.extratags && (
                    <div className="text-xs space-y-1 mb-3">
                      {place.extratags.phone && (
                        <div className="text-blue-600 dark:text-blue-400 font-medium">
                          📞 {place.extratags.phone}
                        </div>
                      )}
                      {place.extratags.website && (
                        <a 
                          href={place.extratags.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline block truncate font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          🌐 Website
                        </a>
                      )}
                      {place.extratags.opening_hours && (
                        <div className="text-green-600 dark:text-green-400 font-medium">
                          🕒 {place.extratags.opening_hours}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <a
                    href={getDirectionsUrl(place)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    Get Directions
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
